import React from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { EventsModalOpenFrom, EventsModalProps } from 'common/module_interface/events/EventsModalsRegistry';
import { useTranslation } from 'react-i18next';
import { Message, Stack, TextArea } from 'common/design-system/components-v2';
import { getEventsNamespace } from 'modules/events/initialize.i18n';
import { ComplianceFindingComment } from 'common/module_interface/events/complianceFindingApi.interface';
import { getNotificationsService } from 'common/interface/services';
import { NotificationType } from 'common/interface/notifications';
import { useMutateEventComment } from 'modules/events/services/complianceFindingApi/hooks/useMutateEventComment';

interface FormValues {
    comment: string;
}
const commentMaxLength = 200;

const CommentAction: React.FC<EventsModalProps> = ({
    closeModal,
    selectedRows,
    resetSelectedRows,
    requestNewData,
    openFrom,
}) => {
    const { t } = useTranslation(getEventsNamespace('actions'));

    const onSuccess = (response: ComplianceFindingComment.Response) => {
        if (response.hasErrors) {
            const failedCount = Object.keys(response.failedItems).length;
            getNotificationsService().addNotification({
                text: t('COMMENT.NOTIFICATIONS.PARTIAL_ERROR', { failedCount, eventsCount: selectedRows.length }),
                type: NotificationType.WARNING,
            });
        } else {
            getNotificationsService().addNotification({
                text: t('COMMENT.NOTIFICATIONS.SUCCESS', { count: selectedRows.length }),
                type: NotificationType.SUCCESS,
            });
        }
        if (openFrom === EventsModalOpenFrom.TABLE && resetSelectedRows) {
            resetSelectedRows();
        }
        closeModal();
        requestNewData?.();
    };

    const onError = () => {
        getNotificationsService().addNotification({
            text: t('COMMENT.NOTIFICATIONS.ERROR', { count: selectedRows.length }),
            type: NotificationType.ERROR,
        });
    };

    const { addComment, isLoading } = useMutateEventComment({ onSuccess, onError });

    const inputValidations: { [input: string]: RegisterOptions } = React.useMemo(
        () => ({
            comment: {
                required: t('COMMENT.INPUTS.COMMENT.ERRORS.REQUIRED'),
                maxLength: {
                    value: commentMaxLength,
                    message: t('COMMENT.INPUTS.COMMENT.ERRORS.MAX_LENGTH', { value: commentMaxLength }),
                },
            },
        }),
        [t],
    );

    const { handleSubmit, control } = useForm<FormValues>({
        defaultValues: {
            comment: '',
        },
    });

    const submitUpdate = async (data: FormValues) => {
        const { comment } = data;
        if (comment) {
            void addComment(selectedRows, comment);
        }
    };

    return (
        <Message
            title={t('COMMENT.TITLE')}
            text={selectedRows.length > 1 ? t('COMMENT.TEXT', { count: selectedRows.length }) : undefined}
            submitBtnText={t('COMMENT.BUTTONS.SUBMIT')}
            width='lg'
            onClose={closeModal}
            onCancel={closeModal}
            onConfirm={handleSubmit((data) => submitUpdate(data))}
            isLoading={isLoading}
            dataAid='events-comment-action'
        >
            <Controller
                name='comment'
                rules={inputValidations['comment']}
                control={control}
                render={({ field, fieldState }) => (
                    <Stack>
                        <TextArea
                            label={t('COMMENT.INPUTS.COMMENT.LABEL')}
                            placeholder={t('COMMENT.INPUTS.COMMENT.PLACEHOLDER')}
                            rows={4}
                            required
                            fullWidth
                            isError={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            maxLength={commentMaxLength}
                            autoFocus
                            {...field}
                        />
                    </Stack>
                )}
            />
        </Message>
    );
};

export default CommentAction;
